/**
 * The support-mediated ownership recovery (ADR-0055 §4) as the admin app sees
 * it: platform staff's "Assign owner" page, and the banner every owner sees
 * while a recovery waits out its 72 hours.
 */

export type RecoveryProof = 'stripe_payer' | 'recovery_contact' | 'firm_domain_email';
export type RecoveryCaseStatus =
  'awaiting_second_approval' | 'approved' | 'completed' | 'cancelled' | 'expired' | 'reversed';

/** The react-query key of the owner banner's list. */
export const PENDING_RECOVERIES_KEY = ['tenant', 'ownership-recoveries'];

/** The fixed checklist staff verify OUT OF BAND (at least two). */
export const PROOFS: { value: RecoveryProof; label: string }[] = [
  { value: 'stripe_payer', label: 'Stripe payer confirmed' },
  { value: 'recovery_contact', label: 'Recovery contact agreed' },
  { value: 'firm_domain_email', label: 'Firm-domain email confirmed' },
];

export const STATUS_LABELS: Record<RecoveryCaseStatus, string> = {
  awaiting_second_approval: 'Needs a second staff member’s approval',
  approved: 'Approved — waiting 72 hours',
  completed: 'Completed',
  cancelled: 'Cancelled',
  expired: 'Expired',
  reversed: 'Reversed',
};

/** One owner's activity, so staff can see the firm really is locked out. */
export interface OwnerActivity {
  actorId: string;
  email: string;
  lastSignInAt: string | null;
  lastSeenAt: string | null;
  mfaEnrolled: boolean;
}

/** A firm found by id, name or a member's email (GET /platform/ownership-recovery/firms). */
export interface FirmMatch {
  tenantId: string;
  name: string | null;
}

/** Where the person to be assigned stands in the firm (shown before assigning). */
export interface AssigneeStanding {
  email: string;
  exists: boolean;
  tenantRole: 'owner' | 'rooms_admin' | null;
  emailVerified: boolean;
  mfaEnrolled: boolean;
  disabled: boolean;
  buyerTeam: boolean;
  boundSeller: boolean;
  roomGrants: number;
  /** In a room's buyer funnel, or a signer of a room document. */
  dealRecords: boolean;
  /** Why they cannot be assigned, or null. */
  refusal: string | null;
}

/** One firm in full (GET /platform/ownership-recovery/firms/:tenantId). */
export interface FirmDetail {
  tenantId: string;
  name: string;
  owners: OwnerActivity[];
  recoveryContact: string | null;
  inFlight: { id: string; status: RecoveryCaseStatus } | null;
  assignee: AssigneeStanding | null;
  /** The Stripe payer of record: found, none on file, or the lookup failed. */
  payer: { lookup: 'ok' | 'none' | 'failed'; email: string | null };
}

/** Plain words for why a person cannot be assigned. */
export const REFUSAL_LABELS: Record<string, string> = {
  staff_mailbox: 'This is a Sweetview staff address.',
  staff_domain: 'This address is on a Sweetview staff domain.',
  assignee_is_owner: 'Already an owner of this firm.',
  assignee_other_side:
    'On the other side of the firm’s deals (a buyer team, a bound seller, buyer/seller room access, a buyer funnel or a document to sign).',
};

/** A case as staff see it. */
export interface StaffRecoveryCase {
  id: string;
  tenantId: string;
  firmName: string | null;
  assigneeEmail: string;
  proofs: RecoveryProof[];
  proofNote: string;
  reason: string;
  status: RecoveryCaseStatus;
  twoPerson: boolean;
  requestedByEmail: string;
  requestedAt: string;
  approvedByEmail: string | null;
  approvedAt: string | null;
  effectiveAt: string | null;
  noticesSentAt: string | null;
  noticeAttempts: number;
  noticesFailedAt: string | null;
  completedAt: string | null;
  reversalRequestedByEmail: string | null;
  reversalRequestedAt: string | null;
  reversalReason: string | null;
  closedAt: string | null;
  closedByEmail: string | null;
  closeReason: string | null;
  reversibleUntil: string | null;
}

/** A recovery waiting out its 72 hours, as an owner sees it. */
export interface PendingRecovery {
  id: string;
  assigneeEmail: string;
  approvedAt: string;
  effectiveAt: string;
}

export function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
